import React, { useEffect, useState } from "react";
import { Banner, Button, Checkbox, ModalV2 } from "@jsq/ds/core";
import * as styles from "./styles_vanilla.css";

export type ConvertToAllClientsModalProps = {
  /** Name of the space being converted; shown in the copy and scopes the consent. */
  spaceName: string;
  /** How many clients the space is currently scoped to, for the "what changes" line. */
  clientCount: number;
  /**
   * Confirmed. This STAGES the conversion — it does not persist anything. The
   * write happens when the editor is saved, which is why the copy says "when you
   * save" rather than claiming the change has already been made.
   */
  onConfirm: () => void;
  onClose: () => void;
};

/**
 * Confirms the one-way conversion of a client-scoped Space to all-clients
 * (DV-501).
 *
 * The backend treats `appliesToAllClients: true` as a one-way door (DV-500) and
 * drops the explicit client rows, so this is the last point at which the user
 * can back out. Follows the same shape as `PromoteLocalModal`: a warning banner
 * naming the consequence, an acknowledgement checkbox that gates the CTA, and a
 * dismiss path that never acts.
 */
export function ConvertToAllClientsModal({
  spaceName,
  clientCount,
  onConfirm,
  onClose,
}: ConvertToAllClientsModalProps) {
  const [ack, setAck] = useState(false);

  // The acknowledgement names the space, so consent is space-scoped: if the
  // modal is reused for a different space, the previous consent must not carry.
  useEffect(() => {
    setAck(false);
  }, [spaceName]);

  const submit = () => {
    if (!ack) return;
    onConfirm();
  };

  return (
    <ModalV2 show onHide={onClose} size="medium" title="Convert to all clients">
      <ModalV2.Body>
        <div className={styles.convertModalBody}>
          <Banner
            variant="warning"
            title="This can’t be undone once saved"
            description={`Once ${spaceName} applies to all clients, it can never be scoped back to specific clients — not from here, and not through the API.`}
          />
          <p className={styles.convertCopy}>
            {clientCount > 0 ? (
              <>
                <strong>{spaceName}</strong> is currently visible to{" "}
                <strong>
                  {clientCount} {clientCount === 1 ? "client" : "clients"}
                </strong>
                . Converting makes it visible to <strong>every client</strong> in the firm, and
                its current client list is cleared.
              </>
            ) : (
              <>
                <strong>{spaceName}</strong> is scoped to specific clients but has none listed
                yet. Converting makes it visible to <strong>every client</strong> in the firm.
              </>
            )}{" "}
            Nothing changes until you save the space — you can still undo this beforehand.
          </p>
          <div className={styles.convertAck}>
            <Checkbox
              id="convert-all-clients-ack"
              checked={ack}
              onChange={() => setAck((a) => !a)}
              label={
                <span>
                  I understand <strong>{spaceName}</strong> will apply to every client when I
                  save, and that this cannot be reversed.
                </span>
              }
            />
          </div>
        </div>
      </ModalV2.Body>
      <ModalV2.Footer>
        <div className={styles.modalFooter}>
          <Button variant="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" disabled={!ack} onClick={submit}>
            Convert on save
          </Button>
        </div>
      </ModalV2.Footer>
    </ModalV2>
  );
}
